import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Plug, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmSheet } from '@/components/ConfirmSheet';
import { Field } from '@/components/Field';
import { toast } from '@/lib/toast';
import {
  useDeleteItflowSettings,
  useItflowSettings,
  useSaveItflowSettings,
  useTestItflow,
} from './useItflow';

const BLANK = {
  base_url: '',
  default_hourly_rate: '150.00',
  default_currency: 'USD',
  invoice_category_id: 0,
  default_tax_id: 0,
  round_to_seconds: 900,
  enabled: true,
};

/**
 * ITFlow connection.
 *
 * The API key is write-only: it goes in encrypted and only its last four
 * characters ever come back, so the field shows what is saved without being
 * able to leak it.
 */
export function ItflowSettingsPanel() {
  const settings = useItflowSettings();
  const save = useSaveItflowSettings();
  const remove = useDeleteItflowSettings();
  const test = useTestItflow();

  const [form, setForm] = useState(BLANK);
  const [apiKey, setApiKey] = useState('');
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const data = settings.data;

  // Seed once the saved config arrives. Anything typed since stays.
  useEffect(() => {
    if (!data?.configured) return;
    setForm({
      base_url: data.base_url ?? '',
      default_hourly_rate: data.default_hourly_rate,
      default_currency: data.default_currency,
      invoice_category_id: data.invoice_category_id,
      default_tax_id: data.default_tax_id,
      round_to_seconds: data.round_to_seconds,
      enabled: data.enabled,
    });
  }, [
    data?.configured,
    data?.base_url,
    data?.default_hourly_rate,
    data?.default_currency,
    data?.invoice_category_id,
    data?.default_tax_id,
    data?.round_to_seconds,
    data?.enabled,
  ]);

  const set = <K extends keyof typeof BLANK>(key: K, value: (typeof BLANK)[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  if (!data) {
    return <div className="h-48 animate-pulse rounded-lg bg-surface-2" aria-hidden />;
  }

  const canSave = form.base_url.trim() !== '' && (data.configured || apiKey !== '');

  const submit = () => {
    save.mutate(
      { ...form, api_key: apiKey || undefined },
      {
        onSuccess: () => {
          setApiKey('');
          toast.show('ITFlow connection saved');
        },
      },
    );
  };

  return (
    <section className="flex flex-col gap-4 rounded-lg border border-border bg-surface-1 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {data.configured ? (
          <p className="flex items-center gap-2 text-small text-success">
            <Check size={14} strokeWidth={2} aria-hidden />
            Connected to {data.base_url}
          </p>
        ) : (
          <p className="text-small text-text-tertiary">No ITFlow instance connected.</p>
        )}
      </div>

      {/* A past failure, shown as a record rather than a live alarm. */}
      {data.last_error && (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-2 p-3">
          <AlertTriangle
            size={14}
            strokeWidth={1.5}
            className="mt-0.5 shrink-0 text-warning"
            aria-hidden
          />
          <p className="text-small text-text-secondary">{data.last_error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="ITFlow URL"
          value={form.base_url}
          onChange={(e) => set('base_url', e.target.value)}
          placeholder="https://clients.example.com"
          hint="Just the address of the instance, no /api path."
          autoComplete="off"
        />
        <Field
          label="API key"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder={data.api_key_hint ? `•••• ${data.api_key_hint}` : ''}
          hint="Admin → API Keys in ITFlow. The key's user needs Sales and Support permissions."
          autoComplete="new-password"
        />
        <Field
          label="Default hourly rate"
          value={form.default_hourly_rate}
          onChange={(e) => set('default_hourly_rate', e.target.value)}
          hint="Used only when neither Chronos nor ITFlow has a rate for the client."
          inputMode="decimal"
        />
        <Field
          label="Currency"
          value={form.default_currency}
          onChange={(e) => set('default_currency', e.target.value.toUpperCase())}
          maxLength={3}
          autoComplete="off"
        />
        <Field
          label="Invoice category ID"
          type="number"
          value={form.invoice_category_id}
          onChange={(e) => set('invoice_category_id', Number(e.target.value))}
          hint="ITFlow's category for new invoices. 0 for none."
        />
        <Field
          label="Tax ID"
          type="number"
          value={form.default_tax_id}
          onChange={(e) => set('default_tax_id', Number(e.target.value))}
          hint="ITFlow's tax applied to each line. 0 for none."
        />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="itflow-rounding" className="text-small font-medium text-text-secondary">
          Round each line up to
        </label>
        <select
          id="itflow-rounding"
          value={form.round_to_seconds}
          onChange={(e) => set('round_to_seconds', Number(e.target.value))}
          className="min-h-touch max-w-xs rounded border border-border bg-surface-1 px-3 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border"
        >
          <option value={300}>5 minutes</option>
          <option value={360}>6 minutes (0.1h)</option>
          <option value={900}>15 minutes (0.25h)</option>
          <option value={1800}>30 minutes (0.5h)</option>
          <option value={3600}>1 hour</option>
        </select>
        <p className="max-w-prose text-small text-text-tertiary">
          Applied per line, not to the invoice total. Three 10-minute tasks bill 0.75h,
          not 0.5h.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={submit} disabled={!canSave} loading={save.isPending}>
          Save
        </Button>
        {data.configured && (
          <>
            <Button onClick={() => test.mutate()} loading={test.isPending}>
              <Plug size={14} strokeWidth={1.5} aria-hidden />
              Test connection
            </Button>
            <Button variant="ghost" onClick={() => setConfirmDisconnect(true)}>
              <Trash2 size={14} strokeWidth={1.5} aria-hidden />
              Disconnect
            </Button>
          </>
        )}
      </div>

      <ConfirmSheet
        open={confirmDisconnect}
        title="Disconnect ITFlow?"
        body="The API key is deleted. Client and project mappings, and the record of invoices already generated, are kept, disconnecting does not un-send anything."
        confirmLabel="Disconnect"
        danger
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(undefined, {
            onSuccess: () => {
              setConfirmDisconnect(false);
              setApiKey('');
              toast.show('ITFlow disconnected');
            },
          })
        }
        onClose={() => setConfirmDisconnect(false)}
      />
    </section>
  );
}
